import React from "react";
import {ICollectionOverview, IVettingProcessResponse, IVettingResponse} from "../../tevapi/interfaces";
import {Button, Dropdown, MenuProps, Popover, Space, Tooltip, Typography} from "antd";
import {Link, useNavigate} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {TEVService} from "../../tevapi/io";
import {VettingHistogram, VettingProgress} from "./progress";


export const VettingLink: React.FC<{vetting: IVettingResponse}> = ({vetting}) => {
  return (
    <Space align="baseline">
      <VettingProgress progress={vetting.progress} />
      <Popover
        content={
          <VettingHistogram breakdown={vetting.progress.breakdown} />
        }
      >
        <Typography.Title level={4}>
          <Link to={`/vettings/${vetting.pk}/`}>
            Continue to {vetting.process.name}
          </Link>
        </Typography.Title>
      </Popover>
    </Space>
  );
};


export const ContinueVettingList: React.FC<{pk: number | string}> = ({pk}) => {
  const query = useQuery({
    queryKey: ["vetting-list-by-collection", pk],
    queryFn: async (): Promise<IVettingResponse[]> => {
      const response = await TEVService.get(
        `/vettings/`,
        {params: {
          collection_id: pk
        }}
      );
      return response.data;
    },
  });

  if (query.isLoading || query.data === undefined || query.data.length == 0) {
    return null;
  }
  return (
    <Space direction="vertical" size="small">
      {query.data.map((vetting) => <VettingLink key={vetting.pk} vetting={vetting} />)}
    </Space>
  );
};

export const StartVetting: React.FC<{pk: number | string}> = ({pk}) => {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ["vetting-processes", pk],
    queryFn: async (): Promise<IVettingProcessResponse[]> => {
      const response = await TEVService.get(
        `/vetting-processes/`,
        {
          params: {
            available_to: pk
          }
        }
      );
      return response.data;
    }
  });
  if (query.isLoading || query.data === undefined) {
    return null;
  }

  const items = query.data.map(
    (process) => ({
      key: process.pk,
      label: process.name,
      onSelect: (
        ({key}) => {
          TEVService.post(
            `/vettings/`,
            {process_id: key, collection_id: pk}
          ).then(
          (response) => navigate(`/vettings/${response.data.pk}`)
          );
        }
      )
    })
  );

  return (
    <Dropdown menu={{items}}>
    <Button>Start a Vetting Process</Button>
    </Dropdown>
  );
};

export const vettingLinkList = (collection: ICollectionOverview): MenuProps['items'] => {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ["vetting-processes", collection.pk],
    queryFn: async (): Promise<IVettingProcessResponse[]> => {
      const response = await TEVService.get(
        `/vetting-processes/`,
        {
          params: {
            available_to: collection.pk
          }
        }
      );
      return response.data;
    }
  });
  if (query.isLoading || query.data === undefined) {
    return [];
  }

  const items = query.data.map(
    (process) => ({
      key: process.pk,
      label: process.name,
      onClick: (
        ({key}) => {
          TEVService.post(
            `/vettings/`,
            {process_id: key, collection_id: collection.pk}
          ).then(
          (response) => navigate(`/vettings/${response.data.pk}`)
          );
        }
      )
    })
  );
  return items;
};
